import { useQuery } from '@tanstack/react-query'
import { Star } from 'lucide-react'
import { api } from '@/lib/api'
import { formatDriverRating } from '@/lib/driver-rating'
import { Avatar } from '@/components/ui/avatar'
import { Pill, type PillTone } from '@/components/ui/pill'
import { usePageHeader } from '@/components/page-header-context'

type KycStatus = 'PENDING' | 'KYC_SUBMITTED' | 'APPROVED' | 'REJECTED' | 'SUSPENDED'

interface DriverItem {
  driverId: string
  firstName: string
  lastName: string
  phone: string | null
  kycStatus: KycStatus
  rating: number
  ratingsCount: number
}

interface DriverListResponse {
  data: DriverItem[]
  total: number
}

function statusPill(status: KycStatus): { label: string; tone: PillTone } {
  switch (status) {
    case 'APPROVED':
      return { label: 'APPROUVÉ', tone: 'success' }
    case 'REJECTED':
      return { label: 'REFUSÉ', tone: 'danger' }
    case 'SUSPENDED':
      return { label: 'SUSPENDU', tone: 'danger' }
    case 'KYC_SUBMITTED':
      return { label: 'EN VÉRIFICATION', tone: 'neutral' }
    default:
      return { label: 'EN ATTENTE', tone: 'neutral' }
  }
}

function useDrivers() {
  return useQuery<DriverListResponse>({
    queryKey: ['drivers', 'all'],
    queryFn: async () => {
      const { data } = await api.get('/admin/drivers?take=100')
      return data
    },
    staleTime: 30_000,
    gcTime: 5 * 60 * 1000,
    retry: 2,
  })
}

export default function DriversPage() {
  const { data, isLoading, isError } = useDrivers()
  const rows = data?.data ?? []

  usePageHeader('Chauffeurs', `${data?.total ?? 0} chauffeur${(data?.total ?? 0) > 1 ? 's' : ''}`)

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="bg-surface rounded-[18px] border border-line overflow-hidden flex-1 flex flex-col min-h-0">
        <div className="grid grid-cols-[2.4fr_1.4fr_1fr] gap-4 px-6 py-3.5 border-b border-line bg-surfaceAlt">
          {['Chauffeur', 'Statut', 'Note'].map((h) => (
            <span key={h} className="text-[11px] font-bold tracking-wide uppercase text-muted">
              {h}
            </span>
          ))}
        </div>

        <div className="flex-1 overflow-auto">
          {isLoading && <p className="p-6 text-muted">Chargement…</p>}
          {isError && <p className="p-6 text-danger">Erreur lors du chargement des chauffeurs.</p>}
          {!isLoading && !isError && rows.length === 0 && (
            <p className="p-8 text-center text-muted">Aucun chauffeur.</p>
          )}
          {!isLoading &&
            !isError &&
            rows.map((d) => {
              const name = `${d.firstName} ${d.lastName}`.trim() || '—'
              const rating = formatDriverRating(d)
              return (
                <div
                  key={d.driverId}
                  className="grid grid-cols-[2.4fr_1.4fr_1fr] gap-4 items-center px-6 py-3.5 border-b border-line last:border-0"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <Avatar name={name} size={40} />
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-ink truncate">{name}</div>
                      <div className="font-mono text-[11.5px] text-muted mt-0.5">
                        {d.phone ?? '—'}
                      </div>
                    </div>
                  </div>
                  <div>
                    <Pill
                      tone={statusPill(d.kycStatus).tone}
                      mono
                      className="!px-2.5 !py-1 text-[11px]"
                    >
                      {statusPill(d.kycStatus).label}
                    </Pill>
                  </div>
                  <div>
                    {rating ? (
                      <span className="inline-flex items-center gap-1.5 font-semibold text-ink">
                        <Star size={14} strokeWidth={2.2} className="text-primary fill-primary" />
                        <span className="font-mono text-[13px]">{rating}</span>
                      </span>
                    ) : (
                      <Pill tone="neutral" className="!px-2.5 !py-1 text-[11px]">
                        Nouveau
                      </Pill>
                    )}
                  </div>
                </div>
              )
            })}
        </div>
      </div>
    </div>
  )
}
